import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react'
import { Fragment } from 'react'
import { Textarea } from '@/components/ui/textarea'
import { parseTimelineSource } from '@/lib/timeline'

// Renders the same class structure as the canonical stored HTML
// (see timelineToDomSpec), so the in-editor preview is pixel-identical to the
// published blog under the shared `.prose .gs-timeline` CSS.
function TimelinePreview({ source }: { source: string }) {
  const eras = parseTimelineSource(source)

  return (
    <section className="gs-timeline" aria-label="Timeline" aria-hidden="true">
      {eras.map((era, eraIndex) => (
        <Fragment key={`era-${eraIndex}`}>
          <div className="gs-timeline__era">
            <span className="gs-timeline__era-label">{era.label}</span>
            {era.span && <span className="gs-timeline__era-span">{era.span}</span>}
          </div>
          <ol className="gs-timeline__items">
            {era.items.map((item, itemIndex) => (
              <li key={`item-${eraIndex}-${itemIndex}`} className="gs-timeline__item">
                <span className="gs-timeline__node" aria-hidden="true" />
                <span className="gs-timeline__year">{item.year}</span>
                <p className="gs-timeline__body">{item.body}</p>
              </li>
            ))}
          </ol>
        </Fragment>
      ))}
    </section>
  )
}

export default function TimelineNodeView({ node, updateAttributes, selected }: NodeViewProps) {
  const source = (node.attrs.source as string) || ''

  return (
    <NodeViewWrapper
      className={`not-prose my-6 overflow-hidden rounded-xl border bg-muted/30 ${
        selected ? 'ring-2 ring-primary/40' : 'border-input'
      }`}
    >
      <div contentEditable={false} className="space-y-3 p-3">
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">
            Timeline source — <code className="rounded bg-muted px-1 py-0.5">## ERA | SPAN</code> and{' '}
            <code className="rounded bg-muted px-1 py-0.5">YEAR | TEXT</code> per line
          </p>
          <Textarea
            value={source}
            onChange={(event) => updateAttributes({ source: event.target.value })}
            rows={Math.min(Math.max(source.split('\n').length + 1, 4), 16)}
            spellCheck={false}
            className="font-mono text-sm"
            placeholder={'## ORIGINS\n1937 | The Museum of Costume Art is founded.'}
          />
        </div>

        <div className="rounded-lg bg-background p-4">
          <TimelinePreview source={source} />
        </div>
      </div>
    </NodeViewWrapper>
  )
}
